// eslint-disable-next-line @altermaind/you-shall-not-pass
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Control, FieldValues, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { convertStringToNumber } from '@/_shared/helpers/convertStringToNumber';
import { useNotification } from '@/_shared/hooks';
import { useWizard } from '@/_shared/hooks/useWizard';
import { QuestionTypeEnum } from '@/swagger/constants/quiz-enums';
import {
  CallResponsePostQuizByIdNext,
  getQuizByIdResultCall,
  postQuizByIdNextCall,
  postQuizStartCall,
} from '../../../state/api';

type UseEmployeeCompleteQuiz = {
  currentStep: (CallResponsePostQuizByIdNext & { stepAlreadySubmitted?: boolean }) | undefined;
  progressiveSteps: { [step: number]: CallResponsePostQuizByIdNext } | undefined;
  control: Control<FieldValues>;
  currentStepInPreview: boolean;
};

/**
 * Hook: `useEmployeeCompleteQuiz`
 *
 * Manages completing a quiz in a step-by-step wizard experience.
 * It integrates:
 * - **React Hook Form** for dynamic form field management and validation
 * - **React Router** to read `submissionId` from route params
 * - **Wizard** (custom) to plug custom Next/Previous handlers and control navigation buttons state
 * - **API** calls for quiz start/next/result
 *
 * ---
 * ## Assumptions / Conventions
 * - `stepNumber` comes from the backend and is **1-based** (starts from 1).
 * - `question.id` values are numeric and (typically) start from 0.
 *
 * ---
 * ## Internal State
 * - **currentStep**
 *   - Represents the current step (the one currently active in the wizard).
 *   - May include `stepAlreadySubmitted` (internal flag) to distinguish steps already submitted in the past.
 * - **progressiveSteps**
 *   - A map `{ [stepNumber]: CallResponsePostQuizByIdNext }` containing the steps already available
 *     (both those received at start time and those appended progressively via the `next` API).
 * - **currentStepInPreview**
 *   - “Feedback preview” mode: after a submission, the current step enters a preview state to show result/enrichment,
 *     and the Next button can be used to move forward to the next step (if available).
 * - **quizCompleted**
 *   - Flag indicating the quiz is finished (no `nextQuestion` available).
 *
 * ---
 * ## End-to-End Flow (Start → Answer → Preview → Next / Result)
 *
 * ### 1) Quiz start
 * - On first render, the hook reads `responsePostQuizStart`.
 * - When the user starts the quiz, the start response may include:
 *   - `nextQuestion` (the step to be completed now)
 *   - **previously submitted steps** (`submittedSteps`) *only if they exist*; otherwise they are not returned.
 * - If `submittedSteps` are present:
 *   - they are normalized and **stored into `progressiveSteps`** (building a stepNumber-indexed map).
 * - In any case:
 *   - the current step (`nextQuestion`) is also added into `progressiveSteps`
 *   - `currentStep` is set to `nextQuestion` (the step to display/answer).
 *
 * ### 2) Filling the form and validation
 * - Fields are registered dynamically using `react-hook-form`.
 * - The **Next** button is disabled when:
 *   - there is an error on `postQuizByIdNext`, or
 *   - the step is not in preview and no valid selection has been made:
 *     - **SINGLE_CHOICE**: a value must exist under key `questionId`
 *     - **MULTIPLE_CHOICE**: at least one checkbox `multipleChoiceAnswer_{questionId}_{optionId}` must be true
 *
 * ### 3) Submitting an answer (API `next`)
 * - When the user clicks Next:
 *   - latest values are read via `watch()`
 *   - `selectedOptionsIds` is built:
 *     - SINGLE_CHOICE: `[value]`
 *     - MULTIPLE_CHOICE: list of optionIds whose checkbox is true
 * - If we are **not** in preview and the step is **not** already submitted:
 *   - the hook calls `postQuizByIdNext` with:
 *     - `id = submissionId`
 *     - `questionId`
 *     - `responseValue` (numeric list)
 *
 * ### 4) Post-submit: feedback preview and progressiveSteps update
 * - On success:
 *   - the current step is put into **`currentStepInPreview = true`** (feedback preview)
 *   - `currentStep` is updated with the received `response`
 *   - the step is **persisted into `progressiveSteps`** (adding/updating the entry for the following step)
 *
 * ### 5) Check `nextQuestion` → continue or end quiz
 * - After submitting via `next`, the hook checks whether `response.nextQuestion` exists:
 *   - **If it exists**: the user stays in the flow and can move forward/backward across steps (also using `progressiveSteps`).
 *   - **If it does NOT exist**: the user has **completed the quiz**.
 *     - at that point, the hook calls `getQuizByIdResult` (result)
 *     - on success it sets `quizCompleted = true`
 *
 * ---
 * ## Wizard navigation (Next / Previous)
 * - The hook registers custom callbacks on the wizard:
 *   - `setCustomOnNext` → uses `onSubmit`
 *   - `setCustomOnPrevious` → uses `onPrevious`
 * - In preview mode, Next can:
 *   - exit preview and move to the next step already available in `progressiveSteps` (if present)
 * - Previous:
 *   - fetches the previous step from `progressiveSteps`
 *   - also handles going back while in preview
 *
 * ---
 * ## Restoring previously submitted answers
 * - When `currentStep.nextQuestion.question` changes, the hook checks whether that question is present in
 *   `responsePostQuizStart.submittedSteps` and, if so, repopulates the form fields:
 *   - MULTIPLE_CHOICE: sets previously selected checkboxes to `true`
 *   - SINGLE_CHOICE: restores the selected value
 *
 * ---
 * ## Return
 * - `currentStep`: current step (question + metadata + feedback/enrichment and optional `stepAlreadySubmitted` flag)
 * - `progressiveSteps`: stepNumber-indexed map of available/completed steps
 * - `control`: React Hook Form control to render dynamic fields
 * - `currentStepInPreview`: whether the current step is in feedback preview mode
 *
 * ### Example Usage:
 * ```tsx
 * const { currentStep, progressiveSteps, control, currentStepInPreview } = useEmployeeCompleteQuiz();
 *
 * if (currentStep) {
 *   console.log('Current Question:', currentStep.nextQuestion.question.text);
 * }
 * ```
 */
export const useEmployeeCompleteQuiz: () => UseEmployeeCompleteQuiz = () => {
  const tShared = useTranslations();
  const { notify } = useNotification();

  const { submissionId } = useParams();
  const {
    execute: executePostQuizByIdNext,
    loading: loadingPostQuizByIdNext,
    error: errorPostQuizByIdNext,
  } = postQuizByIdNextCall.hook();
  const { response: responsePostQuizStart } = postQuizStartCall.hook();
  const { execute: executeGetQuizByIdResult, loading: loadingGetQuizByIdResult } =
    getQuizByIdResultCall.hook();

  const [currentStep, setCurrentStep] = useState<UseEmployeeCompleteQuiz['currentStep']>({
    nextQuestion: {
      question: {
        id: responsePostQuizStart?.nextQuestion.question.id || 0,
        type: responsePostQuizStart?.nextQuestion.question.type || QuestionTypeEnum.SINGLE_CHOICE,
        text: responsePostQuizStart?.nextQuestion.question.text || '',
        maxSelections: responsePostQuizStart?.nextQuestion.question.maxSelections || 0,
        options: responsePostQuizStart?.nextQuestion.question.options || [],
      },
      stepNumber: responsePostQuizStart?.nextQuestion.stepNumber || 0,
      totalSteps: responsePostQuizStart?.nextQuestion.totalSteps || 0,
    },
    answerResult: 'CORRECT',
    enrichment: '',
  });
  const [currentStepInPreview, setCurrentStepInPreview] = useState<boolean>(false);
  const [quizCompleted, setQuizCompleted] = useState<boolean>(false);

  const [progressiveSteps, setProgressiveSteps] = useState<
    { [step: number]: CallResponsePostQuizByIdNext } | undefined
  >(undefined);

  const {
    setCustomOnNext,
    setCustomOnPrevious,
    setNavigationButtonStatus,
    resetWizardNavigationCallbacks,
    resetWizardNavigationButtonStatus,
  } = useWizard();

  const { control, watch, setValue } = useForm<FieldValues>({
    defaultValues: {},
  });

  const dynamicData = watch();

  const isNextButtonDisabled = useMemo(() => {
    if (errorPostQuizByIdNext) {
      return true;
    }
    if (!currentStep) {
      return false;
    }

    if (currentStepInPreview) {
      return false;
    }

    const questionId = `${currentStep.nextQuestion?.question.id}`;

    if (currentStep.nextQuestion.question.type === QuestionTypeEnum.SINGLE_CHOICE) {
      return !(dynamicData as FieldValues)[questionId];
    }
    if (currentStep.nextQuestion.question.type === QuestionTypeEnum.MULTIPLE_CHOICE) {
      const someDataFilled = Object.keys(dynamicData).some(
        key =>
          key.startsWith(`multipleChoiceAnswer_${questionId}_`) &&
          (dynamicData as FieldValues)[key],
      );
      return !someDataFilled;
    }

    return false;
  }, [currentStep, currentStepInPreview, dynamicData, errorPostQuizByIdNext]);

  const onSubmit: NonNullable<Parameters<typeof setCustomOnNext>[0]> = useCallback(
    navigate => {
      if (quizCompleted) {
        navigate?.();
        return;
      }
      if (!currentStep) {
        return;
      }
      // Don't use dynamicData directly to avoid loop crash memoization.
      // ensure we have the latest values
      const data = watch();

      let selectedOptionsIds: string[] | undefined;
      const questionId = `${currentStep.nextQuestion?.question.id}` || '';
      if (currentStep.nextQuestion?.question.type === QuestionTypeEnum.SINGLE_CHOICE) {
        selectedOptionsIds = [(data as FieldValues)[questionId] as string];
      } else {
        // MULTIPLE_CHOICE
        selectedOptionsIds = Object.keys(data)
          .filter(
            key =>
              key.startsWith(`multipleChoiceAnswer_${questionId}_`) && (data as FieldValues)[key],
          )
          .map(key => key.replace(`multipleChoiceAnswer_${questionId}_`, ''));
      }

      if (!currentStepInPreview && !currentStep.stepAlreadySubmitted) {
        executePostQuizByIdNext({
          id: submissionId || '',
          questionId: currentStep.nextQuestion.question.id,
          responseValue:
            selectedOptionsIds
              ?.map(id => convertStringToNumber(id))
              .filter(val => val !== undefined) ?? [],
          successSaga: ({ response }) => {
            if (response) {
              setCurrentStepInPreview(true);
              setCurrentStep({
                ...response,
              });
              // Store the step in progressiveSteps
              setProgressiveSteps(prev => ({
                ...prev,
                [currentStep.nextQuestion.stepNumber + 1]: response,
              }));
            }
            if (!response.nextQuestion) {
              executeGetQuizByIdResult({
                id: submissionId || '',
                successSaga: () => {
                  setQuizCompleted(true);
                },
                errorSaga: () => {
                  notify(tShared('errors.genericApiError'), 'error');
                },
              });
            }
          },
        });
      } else {
        setCurrentStepInPreview(!currentStepInPreview);
        if (!currentStep.nextQuestion) {
          navigate?.();
          return;
        }
        const lastProgressiveStepNumber = Object.keys(progressiveSteps || {}).length;
        let nextStepNumber = currentStep.nextQuestion.stepNumber + 1;
        if (currentStepInPreview) {
          nextStepNumber = currentStep.nextQuestion.stepNumber;
        }
        const nextStep = progressiveSteps?.[
          nextStepNumber
        ] as Required<CallResponsePostQuizByIdNext>;
        setCurrentStep({
          ...nextStep,
          stepAlreadySubmitted: nextStep?.nextQuestion.stepNumber < lastProgressiveStepNumber,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      currentStep,
      executePostQuizByIdNext,
      executeGetQuizByIdResult,
      submissionId,
      watch,
      setCurrentStep,
      currentStepInPreview,
      progressiveSteps,
      setQuizCompleted,
      quizCompleted,
    ],
  );

  const onPrevious: NonNullable<Parameters<typeof setCustomOnPrevious>[0]> = useCallback(
    navigate => {
      // If there's no current step or it's the first step, use wizard navigate
      if (!currentStep || !currentStep.nextQuestion.stepNumber) {
        navigate?.();
        return;
      }

      const previouslySubmittedSteps = {
        ...(progressiveSteps?.[
          currentStep.nextQuestion.stepNumber - 1
        ] as Required<CallResponsePostQuizByIdNext>),
      };
      if (!previouslySubmittedSteps) {
        return;
      }
      if (currentStepInPreview) {
        setCurrentStepInPreview(false);
        setCurrentStep({
          stepAlreadySubmitted: true,
          ...previouslySubmittedSteps,
        });
        return;
      }
      setCurrentStep({
        stepAlreadySubmitted: true,
        ...previouslySubmittedSteps,
      });
    },
    [currentStep, currentStepInPreview, progressiveSteps],
  );

  useEffect(() => {
    const q = currentStep?.nextQuestion?.question;
    if (!q) return;

    const prev = responsePostQuizStart?.submittedSteps?.find(s => s.questionId === q.id);
    if (!prev) return;

    if (q.type === QuestionTypeEnum.MULTIPLE_CHOICE) {
      prev.responseValue?.forEach(v => {
        setValue(`multipleChoiceAnswer_${q.id}_${v}` as never, true as never);
      });
    }

    if (q.type === QuestionTypeEnum.SINGLE_CHOICE) {
      const previousSubmittedAnswers = prev.responseValue?.[0];
      if (previousSubmittedAnswers !== undefined) {
        setValue(String(q.id) as never, `${previousSubmittedAnswers}` as never);
      }
    }
  }, [
    currentStep?.nextQuestion?.question,
    currentStep?.nextQuestion?.question.id,
    responsePostQuizStart?.submittedSteps,
    setValue,
  ]);

  useEffect(() => {
    if (!responsePostQuizStart?.nextQuestion) {
      return;
    }

    const { totalSteps } = responsePostQuizStart.nextQuestion;

    // 1) build progressiveSteps from the answers already submitted
    // In the standard flow, the answer feedback is provided immediately together with the next question data.
    // When restoring a quiz, this standard is not preserved:
    // feedback for previously submitted answers is returned inside `submittedSteps` along with the data
    // of the question it refers to.
    // Therefore, I fix this data mismatch by moving the feedback into the context of the next question.
    const fromAnswers: { [step: number]: CallResponsePostQuizByIdNext } = (
      responsePostQuizStart.submittedSteps ?? []
    ).reduce(
      (acc, answer, index) => {
        const stepNumber = index + 1; // assumes the steps are already in order and that stepNumber is 1-based
        acc[stepNumber] = {
          nextQuestion: {
            ...answer,
            stepNumber: answer.stepNumber,
            totalSteps,
          },
          answerResult:
            responsePostQuizStart.submittedSteps.find(step => step.stepNumber === stepNumber - 1)
              ?.answerResult ?? 'CORRECT',
          enrichment:
            responsePostQuizStart.submittedSteps.find(step => step.stepNumber === stepNumber - 1)
              ?.question.enrichment ?? '',
        };
        return acc;
      },
      {} as { [step: number]: CallResponsePostQuizByIdNext },
    );

    // 2) add the current step (nextQuestion) to progressiveSteps as well
    const nextStepNumber = responsePostQuizStart.nextQuestion.stepNumber;
    const progressive = {
      ...fromAnswers,
      [nextStepNumber]: {
        nextQuestion:
          responsePostQuizStart.nextQuestion as Required<CallResponsePostQuizByIdNext>['nextQuestion'],
        answerResult:
          responsePostQuizStart.submittedSteps.find(step => step.stepNumber === nextStepNumber - 1)
            ?.answerResult ?? 'CORRECT',
        enrichment:
          responsePostQuizStart.submittedSteps.find(step => step.stepNumber === nextStepNumber - 1)
            ?.question.enrichment ?? '',
      },
    };

    setProgressiveSteps(progressive as { [step: number]: CallResponsePostQuizByIdNext });

    // 3) set nextQuestion as currentStep
    setCurrentStep({
      nextQuestion:
        responsePostQuizStart.nextQuestion as Required<CallResponsePostQuizByIdNext>['nextQuestion'],
      answerResult: 'CORRECT',
      enrichment: '',
    });
  }, [responsePostQuizStart]);

  useEffect(() => {
    setCustomOnNext(navigate => onSubmit(navigate));
    setCustomOnPrevious(navigate => onPrevious(navigate));

    return () => {
      // Cleanup function to reset the custom onNext callback
      resetWizardNavigationCallbacks();
    };
  }, [onPrevious, onSubmit, resetWizardNavigationCallbacks, setCustomOnNext, setCustomOnPrevious]);

  useEffect(() => {
    // Update the navigation button status based on form validity
    setNavigationButtonStatus({
      disableNextButton: isNextButtonDisabled,
      isLoadingNextButton: loadingPostQuizByIdNext || loadingGetQuizByIdResult,
    });

    return () => {
      // Cleanup function to reset the navigation button status
      resetWizardNavigationButtonStatus();
    };
  }, [
    isNextButtonDisabled,
    loadingPostQuizByIdNext,
    loadingGetQuizByIdResult,
    resetWizardNavigationButtonStatus,
    setNavigationButtonStatus,
  ]);

  return {
    currentStepInPreview,
    currentStep,
    progressiveSteps,
    control,
  };
};
